import { useCallback, useEffect, useState } from 'react';
import {
  COLOR_MODE_CHANGE_EVENT,
  getCurrentColorMode,
  getStoredColorMode,
  getSystemColorMode,
  setColorMode,
  type ColorMode,
} from '@config/themeBootstrap';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const STORAGE_KEY = 'trendz.colorMode';

/**
 * React hook over the color-mode bootstrap helpers.
 *
 * - Reads the *currently-applied* mode (so it's correct even if mounted after
 *   a programmatic change).
 * - Reacts to OS / browser pref changes ONLY when the user hasn't set an
 *   explicit choice — once a user picks light or dark, that sticks.
 * - Reacts to other tabs changing the choice via the `storage` event.
 */
export function useColorMode() {
  const [mode, setMode] = useState<ColorMode>(getCurrentColorMode);

  const set = useCallback((next: ColorMode) => {
    setColorMode(next);
    setMode(next);
  }, []);

  const toggle = useCallback(() => {
    set(getCurrentColorMode() === 'dark' ? 'light' : 'dark');
  }, [set]);

  // Sync with in-tab changes made by any other useColorMode() instance (e.g.
  // the sidebar toggle, so the MUI theme builder in main.tsx rebuilds). The
  // `storage` event below only covers OTHER tabs.
  useEffect(() => {
    const onModeChange = () => setMode(getCurrentColorMode());
    window.addEventListener(COLOR_MODE_CHANGE_EVENT, onModeChange);
    return () => window.removeEventListener(COLOR_MODE_CHANGE_EVENT, onModeChange);
  }, []);

  // Sync with system preference — only while the user has no explicit choice.
  useEffect(() => {
    const mq = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = () => {
      if (getStoredColorMode() !== null) return; // user has chosen — ignore OS.
      const next = getSystemColorMode();
      setColorMode(next, { persist: false });
      setMode(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Sync across tabs via localStorage — when another tab changes the choice,
  // this tab follows.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next: ColorMode =
        e.newValue === 'dark' || e.newValue === 'light'
          ? e.newValue
          : getSystemColorMode();
      setColorMode(next, { persist: false });
      setMode(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return { mode, set, toggle };
}
